import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ComponentType} from 'react'

import {buildPullRequestsLayoutRoute} from './layout-route'
import {buildPullRequestsCommitsRoute, buildPullRequestsCommitsWithRangeRoute} from './commits-route'
import {
  buildPullRequestsChangesRoute,
  buildPullRequestsChangesWithRangeRoute,
  buildPullRequestsFilesRoute,
  buildPullRequestsFilesWithRangeRoute,
} from './files-changed-routes'
import {buildPullRequestsConversationsRoute} from './conversations-route'
import {buildPullRequestsChecksRoute} from './checks-route'
import {buildPullRequestsOverviewIndexRoute, buildPullRequestsOverviewRoute} from './overview-route'
import {buildPullRequestsFindingsRoute} from './findings-route'
import {buildRepoPullsDashboardContentRoute} from './repo-pulls-dashboard-content-route'
import {buildRepoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'

interface CreatePullRequestsRoutesParams {
  isEnabled: (feature: string) => boolean | undefined
}

export interface PullRequestsRouteComponents {
  PullRequestErrorState: ComponentType
  LayoutEntrypoint: ComponentType
  CommitsEntrypoint: ComponentType
  CommitsWithRangeEntrypoint: ComponentType
  ChangesEntrypoint: ComponentType
  ChangesWithRangeEntrypoint: ComponentType
  FilesEntrypoint: ComponentType
  FilesWithRangeEntrypoint: ComponentType
  ConversationsEntrypoint: ComponentType
  ChecksEntrypoint: ComponentType
  OverviewIndexRouteEntrypoint: ComponentType
  OverviewRouteEntrypoint: ComponentType
  FindingsEntrypoint: ComponentType
  RepoPullsDashboardContent: ComponentType
  RepoPullsDashboardLayout: ComponentType
}

function RepoPullsCleanPathRoute() {
  return null
}

const repoPullsCleanPathRoutes: RouteObject[] = [
  {path: '/:owner/:repo/pulls', index: true, Component: RepoPullsCleanPathRoute},
  {path: '/:owner/:repo/pulls/:login', Component: RepoPullsCleanPathRoute},
  {path: '/:owner/:repo/pulls/created_by/:creator', Component: RepoPullsCleanPathRoute},
  {path: '/:owner/:repo/pulls/assigned/:assignee', Component: RepoPullsCleanPathRoute},
  {path: '/:owner/:repo/pulls/mentioned/:mentioned', Component: RepoPullsCleanPathRoute},
  {path: '/:owner/:repo/pulls/review-requested/:review_requested', Component: RepoPullsCleanPathRoute},
]

/**
 * Builds the Pull Requests route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `pull-requests`-bound singletons directly.
 *
 * `isEnabled` reads the app's server-provided feature state (embedded data). The
 * New Files Changed route set is included when `react_data_router_pull_request_files`
 * is enabled for the request; without it, the condensed commits layout is used.
 */
export function createPullRequestsRoutes(
  appBuilder: DataRouterApplicationBuilder<string>,
  {isEnabled}: CreatePullRequestsRoutesParams,
  components: PullRequestsRouteComponents,
): RouteObject[] {
  const {
    PullRequestErrorState,
    LayoutEntrypoint,
    CommitsEntrypoint,
    CommitsWithRangeEntrypoint,
    ChangesEntrypoint,
    ChangesWithRangeEntrypoint,
    FilesEntrypoint,
    FilesWithRangeEntrypoint,
    ConversationsEntrypoint,
    ChecksEntrypoint,
    OverviewIndexRouteEntrypoint,
    OverviewRouteEntrypoint,
    FindingsEntrypoint,
    RepoPullsDashboardContent,
    RepoPullsDashboardLayout,
  } = components
  // Shared managed error boundary for every PR content subroute. Does NOT update the
  // document title — only the outermost layout boundary may do that, so a broken content
  // pane never clobbers the browser tab title for the rest of the page.
  const errorBoundary = {fallback: <PullRequestErrorState />}
  // Layout boundary: owns the full page view, so it may update the document title.
  const layoutErrorBoundary = {fallback: <PullRequestErrorState />, setDocumentTitleOnResponseError: true}
  const overviewAsIndexRoute = isEnabled('prs_overview_as_index_route') ?? false

  let indexRoute: RouteObject
  if (overviewAsIndexRoute) {
    indexRoute = buildPullRequestsOverviewIndexRoute(appBuilder).toRoute({
      Component: OverviewIndexRouteEntrypoint,
      errorBoundary,
    })
  } else {
    indexRoute = buildPullRequestsConversationsRoute(appBuilder).toRoute({
      Component: ConversationsEntrypoint,
      errorBoundary,
    })
  }

  const children: RouteObject[] = [
    indexRoute,
    buildPullRequestsChecksRoute(appBuilder).toRoute({
      Component: ChecksEntrypoint,
      errorBoundary,
    }),
    // The Rails controller enforces Findings access, while the layout feature state controls
    // whether navigation to this route is exposed in the UI.
    buildPullRequestsFindingsRoute(appBuilder).toRoute({
      Component: FindingsEntrypoint,
      errorBoundary,
    }),
  ]

  // When Overview is the index route it already renders at `/pull/:number`, so the standalone `/overview` route is redundant.
  // When the route is registered, access is managed via the layout data's feature state.
  if (!overviewAsIndexRoute) {
    children.push(
      buildPullRequestsOverviewRoute(appBuilder).toRoute({
        Component: OverviewRouteEntrypoint,
        errorBoundary,
      }),
    )
  }

  children.push(
    buildPullRequestsCommitsRoute(appBuilder).toRoute({
      Component: CommitsEntrypoint,
      errorBoundary,
    }),
    buildPullRequestsCommitsWithRangeRoute(appBuilder).toRoute({
      Component: CommitsWithRangeEntrypoint,
      errorBoundary,
    }),
  )

  // The `react_data_router_pull_request_files` feature flag is set in the controller when the
  // user is enrolled in the New Files Changed feature preview.
  if (isEnabled('react_data_router_pull_request_files')) {
    children.push(
      buildPullRequestsFilesRoute(appBuilder).toRoute({
        Component: FilesEntrypoint,
        errorBoundary,
      }),
      buildPullRequestsFilesWithRangeRoute(appBuilder).toRoute({
        Component: FilesWithRangeEntrypoint,
        errorBoundary,
      }),
      buildPullRequestsChangesRoute(appBuilder).toRoute({
        Component: ChangesEntrypoint,
        errorBoundary,
      }),
      buildPullRequestsChangesWithRangeRoute(appBuilder).toRoute({
        Component: ChangesWithRangeEntrypoint,
        errorBoundary,
      }),
    )
  }

  const routes: RouteObject[] = [
    buildPullRequestsLayoutRoute(appBuilder).toRoute({
      errorBoundary: layoutErrorBoundary,
      Component: LayoutEntrypoint,
      children,
    }),
  ]

  if (isFeatureEnabled('repo_pulls_dashboard_surface')) {
    routes.unshift(
      buildRepoPullsDashboardLayoutRoute(appBuilder).toRoute({
        Component: RepoPullsDashboardLayout,
        errorBoundary: layoutErrorBoundary,
        children: [
          buildRepoPullsDashboardContentRoute(appBuilder).toRoute({
            Component: RepoPullsDashboardContent,
            errorBoundary,
            // These explicit leaves match Rails' clean paths without claiming every
            // future /pulls/* path. The parent owns rendering and query loader data.
            children: repoPullsCleanPathRoutes,
          }),
        ],
      }),
    )
  }

  return routes
}
